import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Field, change } from 'redux-form';
import { t } from 'i18next';
import { AddressBookEntry } from '../AddressBookEntry';
import { isEmail, isRequired, maxChar50 } from '../../../utils/validator';
import {
  FieldGroupLabel,
  PaymentMethod,
  PaymentMethodsContainer,
  AddressBlock,
  BillingAccountsContainer,
  BillToGuide,
} from '../css';
import { AddressTypes, PaymentMethods } from '../../../utils/enums';
import Column from '../../ReactComponent/Column';
import Row from '../../ReactComponent/Row';
import { billingAccountTextFormatter, billingAccountToNumber } from '../../../utils/form';
import { postalValidateCall } from '../index';
import { arrayLengthSafe } from '../../../utils/arrays';
import TextInput from '../../../lib/ui/form_elements/TextInput';
import Dropdown from '../../../lib/ui/form_elements/Dropdown';
import RadioButton from '../../../lib/ui/form_elements/RadioButton';

const Step1 = ({
  formName,
  step,
  formData = {},
  dispatch,
  changeBillTo,
  billingAccounts,
  parentFields = {},
  isLoggedIn,
  addressFieldsRef,
  isNonServiceableLocation,
  openContactPermissionModal,
  fromContactId,
  toContactId,
  shipperName,
  shipperPostalCode,
  consigneePostalCode,
}) => {
  // eslint-disable-next-line no-unused-vars
  const [isAddNewBillingAddress, setIsAddNewBillingAddress] = useState(false);

  const { userAddressType, paymentMethod } = formData;

  const paymentMethods = useMemo(() => (isLoggedIn
    ? []
    : [
      { label: t('PaymentCreditCard'), subLabel: t('PaymentCreditCardDesc'), value: PaymentMethods.CreditCard },
    ]), []);

  const renderBillingInformation = () => {
    if (paymentMethod === PaymentMethods.CreditCard) {
      return undefined;
    }

    return (
      <Row>
        <Column>
          <FieldGroupLabel>{parentFields.categoryTitleStep1 && parentFields.categoryTitleStep1.value}</FieldGroupLabel>
          <BillingAccountsContainer>
            <Row>
              <Column $tablet={6} $mobile={12}>
                <Field
                  name="billTo"
                  label={t('BillTo')}
                  component={Dropdown}
                  options={(billingAccounts || []).map((item) => ({ ...item, value: billingAccountTextFormatter(item) }))}
                  validate={[isRequired]}
                  normalize={billingAccountToNumber}
                  valueField="accountNumber"
                  valueFormatter={billingAccountTextFormatter}
                />
                {isAddNewBillingAddress && (
                  <div style={{ marginTop: '16px' }}>
                    <Field
                      name="billTo"
                      label={t('BillTo')}
                      component={TextInput}
                      validate={[maxChar50, isRequired]}
                    />
                  </div>
                )}
                <FieldGroupLabel>
                  {parentFields.categoryTitle2Step1 && parentFields.categoryTitle2Step1.value}
                </FieldGroupLabel>
                <Field
                  name="quoteDest"
                  component={TextInput}
                  label={t('ContactEmail')}
                  validate={[isEmail, maxChar50, isRequired]}
                />
              </Column>
              {isLoggedIn && (
                <Column $tablet={6} $mobile={12}>
                  <BillToGuide>
                    <h4>{t('BillToGuideTitle')}</h4>
                    <p>{t('BillToGuideFormat')}</p>
                    <dl>
                      <dt>{t('BillToGuideServiceLabel')}</dt>
                      <dd>{t('BillToGuideServiceValue')}</dd>
                      <dt>{t('BillToGuideCurrencyLabel')}</dt>
                      <dd>{t('BillToGuideCurrencyValue')}</dd>
                      <dt>{t('BillToGuideUsageLabel')}</dt>
                      <dd>{t('BillToGuideUsageValue')}</dd>
                    </dl>
                  </BillToGuide>
                </Column>
              )}
            </Row>
          </BillingAccountsContainer>
        </Column>
      </Row>
    );
  };

  const hasSingularAccount = isLoggedIn && arrayLengthSafe(billingAccounts) === 1;

  if (hasSingularAccount && formData.billTo !== billingAccounts[0].accountNumber) {
    dispatch(change(formName, 'billTo', billingAccounts[0].accountNumber));
    changeBillTo(billingAccounts[0].accountNumber);
  }

  if (step === 0) {
    return (
      <>
        <Row ref={addressFieldsRef}>
          <AddressBlock className="col-md-6">
            <FieldGroupLabel>
              {t('ShipperDetailsTitle')}
            </FieldGroupLabel>
            <AddressBookEntry
              contactType={AddressTypes.SHIPPER}
              formName={formName}
              userAddressType={userAddressType}
              validator={postalValidateCall}
              companyNameOptional
              isNonServiceableLocation={isNonServiceableLocation}
              openContactPermissionModal={openContactPermissionModal}
              defaultContactId={fromContactId}
              defaultName={shipperName}
              defaultPostalCode={shipperPostalCode}
            />
          </AddressBlock>
          <AddressBlock className="col-md-6">
            <FieldGroupLabel>
              {t('ConsigneeDetailsTitle')}
            </FieldGroupLabel>
            <AddressBookEntry
              contactType={AddressTypes.CONSIGNEE}
              formName={formName}
              userAddressType={userAddressType}
              validator={postalValidateCall}
              companyNameOptional
              isNonServiceableLocation={isNonServiceableLocation}
              openContactPermissionModal={openContactPermissionModal}
              defaultContactId={toContactId}
              defaultPostalCode={consigneePostalCode}
            />
          </AddressBlock>
        </Row>

        <PaymentMethod>
          {!isLoggedIn && (
            <>
              <FieldGroupLabel>
                {t('PaymentMethod')}
              </FieldGroupLabel>
              <PaymentMethodsContainer loggedIn={isLoggedIn}>
                <Field
                  name="paymentMethod"
                  component={RadioButton}
                  options={paymentMethods}
                  checkPayment={formData.paymentMethod}
                />
              </PaymentMethodsContainer>
            </>
          )}

          {isLoggedIn ? renderBillingInformation() : (paymentMethod && renderBillingInformation())}

          {(!isLoggedIn && (!paymentMethod || paymentMethod === PaymentMethods.CreditCard)) && (
            <Row>
              <Column $tablet={6} $mobile={12}>
                <FieldGroupLabel>
                  {parentFields.categoryTitle2Step1 && parentFields.categoryTitle2Step1.value}
                </FieldGroupLabel>
                <Field
                  name="quoteDest"
                  component={TextInput}
                  label={t('ContactEmail')}
                  validate={[isEmail, maxChar50, isRequired]}
                />
              </Column>
            </Row>
          )}
        </PaymentMethod>
      </>
    );
  }

  return null;
};

Step1.propTypes = {
  formName: PropTypes.string,
  step: PropTypes.number,
  formData: PropTypes.object,
  dispatch: PropTypes.func,
  changeBillTo: PropTypes.func,
  billingAccounts: PropTypes.array,
  parentFields: PropTypes.object,
  isLoggedIn: PropTypes.bool,
  addressFieldsRef: PropTypes.object,
  isNonServiceableLocation: PropTypes.func,
  openContactPermissionModal: PropTypes.func,
  fromContactId: PropTypes.string,
  toContactId: PropTypes.string,
  shipperName: PropTypes.string,
  shipperPostalCode: PropTypes.string,
  consigneePostalCode: PropTypes.string,
};

export default Step1;
